import { branchRecord, clientRecord } from '@occ/contracts';
import type { BaseRecord, CrudFilter, DataProvider } from '@refinedev/core';
import { request, type Query } from '../api/http';
import { API_URL } from '../config/env';
import { parseNodeRow } from './nodes';

/**
 * `dataProvider` de Refine contra el Módulo 0.
 *
 * No se usa `@refinedev/simple-rest` porque el backend devuelve las colecciones
 * como arrays desnudos —sin envoltorio ni cabecera `x-total-count`— y porque
 * cada una se valida con su esquema de `@occ/contracts`: si el backend cambia
 * una forma, el panel falla en el borde y no tres componentes más adentro.
 *
 * Consecuencia de no haber total: la paginación es de cliente. Con cientos de
 * nodos basta; cuando el backend pagine, se le añade el envoltorio aquí.
 */

type Parser<T> = { parse: (value: unknown) => T };

const ITEM_SCHEMAS: Record<string, Parser<unknown> | undefined> = {
  clients: clientRecord,
  branches: branchRecord,
  nodes: { parse: parseNodeRow },
};

const parseCollection = (resource: string, payload: unknown) => {
  const schema = ITEM_SCHEMAS[resource];
  const items = Array.isArray(payload) ? payload : [];
  const data = schema ? items.map((item) => schema.parse(item)) : items;
  return { data, total: data.length };
};

/**
 * Traduce los filtros de Refine a parámetros de consulta. Solo se soportan los
 * que el backend entiende (`eq` sobre ids y estado, `contains` sobre alias):
 * aceptar más en silencio produciría listas mal filtradas que parecen correctas.
 */
const toQuery = (filters: CrudFilter[] | undefined): Query => {
  const query: Query = {};
  for (const filter of filters ?? []) {
    if (!('field' in filter)) continue;
    if (filter.value === undefined || filter.value === null || filter.value === '') continue;

    if (filter.operator === 'eq') {
      query[filter.field] = String(filter.value);
      continue;
    }
    if (filter.operator === 'contains' && (filter.field === 'alias' || filter.field === 'search')) {
      query.search = String(filter.value);
    }
  }
  return query;
};

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  async getList({ resource, pagination, filters }) {
    const payload = await request<unknown>({
      method: 'GET',
      path: `/${resource}`,
      query: toQuery(filters),
    });
    const { data, total } = parseCollection(resource, payload);

    if (pagination?.mode === 'off' || !pagination) return { data: data as never[], total };
    const pageSize = pagination.pageSize ?? 50;
    const start = ((pagination.current ?? 1) - 1) * pageSize;
    return { data: data.slice(start, start + pageSize) as never[], total };
  },

  async getOne({ resource, id }) {
    const payload = await request<BaseRecord>({ method: 'GET', path: `/${resource}/${id}` });
    return { data: payload as never };
  },

  async create({ resource, variables }) {
    const payload = await request<BaseRecord>({
      method: 'POST',
      path: `/${resource}`,
      body: variables,
    });
    return { data: payload as never };
  },

  async update({ resource, id, variables }) {
    const payload = await request<BaseRecord>({
      method: 'PATCH',
      path: `/${resource}/${id}`,
      body: variables,
    });
    return { data: payload as never };
  },

  async deleteOne({ resource, id }) {
    await request<void>({ method: 'DELETE', path: `/${resource}/${id}` });
    return { data: { id } as never };
  },

  async custom({ url, method, payload, query }) {
    const data = await request<BaseRecord>({
      method: (method?.toUpperCase() ?? 'GET') as 'GET',
      path: url.startsWith('/') ? url : `/${url}`,
      body: payload,
      query: query as Query | undefined,
    });
    return { data: data as never };
  },
};
